import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
// QRCode será renderizado via API externa ou texto
import './ConfigurarWhatsApp.css';

/**
 * Página para Admin configurar e gerenciar WhatsApp
 */

const ConfigurarWhatsApp = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testando, setTestando] = useState(false);
  const [reconectando, setReconectando] = useState(false);
  const [desconectando, setDesconectando] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editandoConfig, setEditandoConfig] = useState(false);
  const [salvandoConfig, setSalvandoConfig] = useState(false);
  const [whatsappGroupNumber, setWhatsappGroupNumber] = useState('');
  const [whatsappIndividualNumber, setWhatsappIndividualNumber] = useState('');

  // Debug: log do status para diagnóstico
  useEffect(() => {
    if (status) {
      console.log('🔍 Status WhatsApp:', {
        connected: status.connected,
        hasQRCode: status.hasQRCode,
        enabled: status.enabled,
        destinationNumber: status.destinationNumber,
        destinationSource: status.destinationSource,
        qrCode: status.qrCode ? 'Presente' : 'Ausente'
      });
      
      // Atualizar campos de configuração quando status carregar
      if (status.destinationNumber && !editandoConfig) {
        // O destinationNumber já vem sem o sufixo @g.us ou @c.us
        if (status.destinationType === 'group') {
          setWhatsappGroupNumber(status.destinationNumber + '@g.us');
          setWhatsappIndividualNumber('');
        } else if (status.destinationType === 'individual') {
          setWhatsappIndividualNumber(status.destinationNumber + '@c.us');
          setWhatsappGroupNumber('');
        }
      } else if (!status.destinationNumber && !editandoConfig) {
        // Se não tem destino configurado, limpar campos
        setWhatsappGroupNumber('');
        setWhatsappIndividualNumber('');
      }
    }
  }, [status, editandoConfig]);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    
    carregarStatus();
    
    // Polling inteligente:
    // - Inicializando: verificar a cada 5s (para pegar QR Code rapidamente)
    // - Tem QR Code mas não conectado: verificar a cada 3s (para detectar scan rapidamente)
    // - Conectado: verificar a cada 60s (só para detectar queda de conexão)
    // - Desconectado sem QR: verificar a cada 10s
    let intervalTime = 10000; // padrão: 10s
    
    if (status?.initializing) {
      intervalTime = 5000; // 5s durante inicialização
    } else if (status?.hasQRCode && !status?.connected) {
      intervalTime = 3000; // 3s quando tem QR Code (aguardando scan)
    } else if (status?.connected) {
      intervalTime = 60000; // 60s quando conectado
    }
    
    const interval = setInterval(() => {
      carregarStatus();
    }, intervalTime);
    
    return () => clearInterval(interval);
  }, [isAdmin, navigate, status?.connected, status?.initializing, status?.hasQRCode]);

  const carregarStatus = async () => {
    try {
      setError('');
      const response = await apiClient.get('/whatsapp/status');
      setStatus(response.data);
      // Não logar durante inicialização (evita poluição)
      if (!response.data.initializing) {
        console.log('📊 Status WhatsApp atualizado:', {
          connected: response.data.connected,
          hasQRCode: response.data.hasQRCode,
          enabled: response.data.enabled,
        });
      }
    } catch (err) {
      console.error('Erro ao carregar status:', err);
      setError(err.response?.data?.error || 'Erro ao carregar status do WhatsApp');
    } finally {
      setLoading(false);
    }
  };

  const handleTestar = async () => {
    try {
      setTestando(true);
      setError('');
      setSuccess('');
      const response = await apiClient.post('/whatsapp/test');
      setSuccess('Mensagem de teste enviada com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Erro ao testar:', err);
      setError(err.response?.data?.error || 'Erro ao enviar mensagem de teste');
    } finally {
      setTestando(false);
    }
  };

  const handleReconectar = async () => {
    try {
      setReconectando(true);
      setError('');
      setSuccess('');
      const response = await apiClient.post('/whatsapp/reconnect');
      setSuccess('Reconexão iniciada. Aguarde alguns segundos para o QR Code aparecer...');
      
      // Atualizar status após alguns segundos para pegar o QR Code quando for gerado
      // Reduzir número de chamadas para não sobrecarregar
      setTimeout(() => carregarStatus(), 5000);
      setTimeout(() => {
        carregarStatus();
        setSuccess('');
      }, 10000);
    } catch (err) {
      console.error('Erro ao reconectar:', err);
      setError(err.response?.data?.error || 'Erro ao reconectar WhatsApp');
    } finally {
      setReconectando(false);
    }
  };

  const handleDesconectar = async () => {
    if (!window.confirm('Tem certeza que deseja desconectar o WhatsApp? Você precisará escanear o QR Code novamente para reconectar.')) {
      return;
    }

    try {
      setDesconectando(true);
      setError('');
      setSuccess('');
      const response = await apiClient.post('/whatsapp/disconnect');
      setSuccess('WhatsApp desconectado com sucesso! Use "Reconectar" para gerar um novo QR Code.');
      // Atualizar status imediatamente e depois novamente após 2 segundos
      await carregarStatus();
      setTimeout(() => {
        carregarStatus();
        setSuccess('');
      }, 2000);
    } catch (err) {
      console.error('Erro ao desconectar:', err);
      setError(err.response?.data?.error || 'Erro ao desconectar WhatsApp');
    } finally {
      setDesconectando(false);
    }
  };

  if (loading && !status) {
    return <div className="container">Carregando status do WhatsApp...</div>;
  }

  return (
    <div className="container configurar-whatsapp-container">
      <div className="page-header">
        <h1>📱 Configurar WhatsApp</h1>
        <button onClick={() => navigate('/admin/menu')} className="btn btn-secondary">
          ← Voltar
        </button>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <div className="whatsapp-status-card">
        <h2>Status da Conexão</h2>
        
        <div className="status-info">
          <div className="status-item">
            <span className="status-label">WhatsApp Habilitado:</span>
            <span className={`status-value ${status?.enabled ? 'enabled' : 'disabled'}`}>
              {status?.enabled ? '✅ Sim' : '❌ Não'}
            </span>
          </div>

          <div className="status-item">
            <span className="status-label">Status da Conexão:</span>
            <span className={`status-value ${status?.connected ? 'connected' : 'disconnected'}`}>
              {status?.connected ? '✅ Conectado' : '❌ Desconectado'}
            </span>
          </div>

          {status?.destinationNumber && (
            <div className="status-item">
              <span className="status-label">Destino:</span>
              <span className="status-value">
                {status.destinationNumber} ({status.destinationType === 'group' ? 'Grupo' : 'Individual'})
                {status.destinationSource && (
                  <span style={{ fontSize: '0.85em', color: '#666', marginLeft: '8px' }}>
                    ({status.destinationSource === 'database' ? '📁 Banco de dados' : '🔧 Variável de ambiente'})
                  </span>
                )}
              </span>
            </div>
          )}
        </div>

        {!status?.enabled && (
          <div className="warning-box">
            <p>⚠️ WhatsApp está desabilitado. Configure <code>WHATSAPP_ENABLED=true</code> no arquivo <code>.env</code> do backend.</p>
          </div>
        )}

        {status?.enabled && !status?.destinationNumber && (
          <div className="warning-box">
            <p>⚠️ Número de destino não configurado. Configure abaixo ou use variáveis de ambiente.</p>
          </div>
        )}

        {/* Seção de Configuração de Número */}
        <div style={{ marginTop: '20px', padding: '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f9f9f9' }}>
          <h3 style={{ marginTop: 0 }}>⚙️ Configuração de Destino</h3>
          <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '15px' }}>
            Configure o número do grupo ou individual aqui. Se deixar vazio, usará a variável de ambiente.
            <br />
            <strong>Formato:</strong> Grupo: <code>5511999999999@g.us</code> | Individual: <code>5511999999999@c.us</code>
          </p>

          {!editandoConfig ? (
            <div>
              <div style={{ marginBottom: '10px' }}>
                <strong>Grupo:</strong> {whatsappGroupNumber || '(não configurado - usando ENV)'}
              </div>
              <div style={{ marginBottom: '15px' }}>
                <strong>Individual:</strong> {whatsappIndividualNumber || '(não configurado - usando ENV)'}
              </div>
              <button
                onClick={() => setEditandoConfig(true)}
                className="btn btn-secondary"
              >
                ✏️ Editar Configuração
              </button>
            </div>
          ) : (
            <div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Número do Grupo (formato: 5511999999999@g.us):
                </label>
                <input
                  type="text"
                  value={whatsappGroupNumber}
                  onChange={(e) => setWhatsappGroupNumber(e.target.value)}
                  placeholder="5511999999999@g.us"
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                />
                <small style={{ color: '#666' }}>Deixe vazio para usar variável de ambiente</small>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Número Individual (formato: 5511999999999@c.us):
                </label>
                <input
                  type="text"
                  value={whatsappIndividualNumber}
                  onChange={(e) => setWhatsappIndividualNumber(e.target.value)}
                  placeholder="5511999999999@c.us"
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                />
                <small style={{ color: '#666' }}>Deixe vazio para usar variável de ambiente</small>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={async () => {
                    try {
                      setSalvandoConfig(true);
                      setError('');
                      setSuccess('');
                      
                      const payload = {};
                      if (whatsappGroupNumber.trim() === '') {
                        payload.whatsapp_group_number = null;
                      } else {
                        payload.whatsapp_group_number = whatsappGroupNumber.trim();
                      }
                      
                      if (whatsappIndividualNumber.trim() === '') {
                        payload.whatsapp_individual_number = null;
                      } else {
                        payload.whatsapp_individual_number = whatsappIndividualNumber.trim();
                      }

                      await apiClient.put('/whatsapp/config', payload);
                      setSuccess('Configuração salva com sucesso!');
                      setEditandoConfig(false);
                      setTimeout(() => {
                        carregarStatus();
                        setSuccess('');
                      }, 2000);
                    } catch (err) {
                      console.error('Erro ao salvar configuração:', err);
                      setError(err.response?.data?.error || 'Erro ao salvar configuração');
                    } finally {
                      setSalvandoConfig(false);
                    }
                  }}
                  className="btn btn-primary"
                  disabled={salvandoConfig}
                >
                  {salvandoConfig ? 'Salvando...' : '💾 Salvar'}
                </button>
                <button
                  onClick={() => {
                    setEditandoConfig(false);
                    // Restaurar valores do status
                    if (status?.destinationNumber) {
                      if (status.destinationType === 'group') {
                        setWhatsappGroupNumber(status.destinationNumber + '@g.us');
                        setWhatsappIndividualNumber('');
                      } else if (status.destinationType === 'individual') {
                        setWhatsappIndividualNumber(status.destinationNumber + '@c.us');
                        setWhatsappGroupNumber('');
                      }
                    } else {
                      setWhatsappGroupNumber('');
                      setWhatsappIndividualNumber('');
                    }
                  }}
                  className="btn btn-secondary"
                  disabled={salvandoConfig}
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>

        {status?.hasQRCode && status?.qrCode && (
          <div className="qr-code-section">
            <h3>📱 Escaneie o QR Code com seu WhatsApp</h3>
            <p>Abra o WhatsApp no seu celular, vá em "Dispositivos conectados" e escaneie este código:</p>
            <div className="qr-code-container">
              <img 
                src={`${apiClient.defaults.baseURL.replace('/api', '')}/api/whatsapp/qrcode-image?t=${Date.now()}`}
                alt="QR Code WhatsApp"
                style={{ maxWidth: '100%', height: 'auto', border: '2px solid #333', borderRadius: '8px' }}
                onError={(e) => {
                  console.error('❌ Erro ao carregar QR Code do backend:', e);
                  // Fallback para API externa se o backend falhar
                  e.target.src = `https://api.qrserver.com/v1/create-qr-code/?size=256x256&data=${encodeURIComponent(status.qrCode)}`;
                }}
                onLoad={() => {
                  console.log('✅ QR Code carregado com sucesso do backend');
                }}
              />
            </div>
            <p className="qr-instructions">
              1. Abra WhatsApp no celular<br />
              2. Menu → Dispositivos conectados<br />
              3. Conectar um dispositivo<br />
              4. Escaneie o código acima
            </p>
          </div>
        )}

        {status?.initializing && (
          <div className="warning-box">
            <p>⏳ WhatsApp está inicializando...</p>
            <p>Aguarde alguns segundos. O QR Code será gerado em breve.</p>
          </div>
        )}

        {!status?.connected && !status?.initializing && status?.enabled && !status?.hasQRCode && (
          <div className="warning-box">
            <p>⏳ QR Code ainda não foi gerado.</p>
            <p>Clique em "Reconectar" para gerar um novo QR Code. Aguarde alguns segundos após clicar.</p>
          </div>
        )}

        {status?.connected && (
          <div className="success-box">
            <p>✅ WhatsApp conectado e pronto para enviar mensagens!</p>
            <p>As mensagens serão enviadas automaticamente quando você registrar um nocaute.</p>
          </div>
        )}

        <div className="action-buttons">
          <button
            onClick={carregarStatus}
            className="btn btn-secondary"
            disabled={loading}
          >
            {loading ? 'Atualizando...' : '🔄 Atualizar Status'}
          </button>

          {status?.connected && (
            <>
              <button
                onClick={handleTestar}
                className="btn btn-primary"
                disabled={testando}
              >
                {testando ? 'Enviando...' : '🧪 Enviar Mensagem de Teste'}
              </button>
              <button
                onClick={handleDesconectar}
                className="btn btn-danger"
                disabled={desconectando}
              >
                {desconectando ? 'Desconectando...' : '🔌 Desconectar'}
              </button>
            </>
          )}

          {!status?.connected && status?.enabled && (
            <button
              onClick={handleReconectar}
              className="btn btn-warning"
              disabled={reconectando}
            >
              {reconectando ? 'Reconectando...' : '🔄 Reconectar'}
            </button>
          )}
        </div>
      </div>

      <div className="info-card">
        <h2>ℹ️ Como Funciona</h2>
        <ul>
          <li>Quando você registrar um nocaute, uma mensagem será enviada automaticamente no WhatsApp</li>
          <li>Se marcar "Eliminou", a mensagem será diferente indicando eliminação</li>
          <li>O sistema tenta enviar uma imagem junto com a mensagem (se disponível)</li>
          <li>Se o WhatsApp não estiver conectado, o nocaute ainda será registrado (apenas não enviará mensagem)</li>
        </ul>
      </div>
    </div>
  );
};

export default ConfigurarWhatsApp;
